import type {
  ShopperAiTagGroupId,
  ShopperAiTagId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import type { RedoBadgeProps } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
} from "@redotech/redo-web/tags/tag-pill";

/**
 * Buckets `currentTags` by tag group, preserving the `tagGroups` ordering.
 * Any tag whose group isn't in `tagGroups` falls into a trailing header-less
 * bucket so tags are never dropped from the display.
 */
export function groupCurrentTagsByGroup(
  currentTags: readonly TagResponse[],
  tagGroups: readonly TagGroupResponse[],
): { group: TagGroupResponse | undefined; tags: TagResponse[] }[] {
  const buckets: {
    group: TagGroupResponse | undefined;
    tags: TagResponse[];
  }[] = [];
  const remaining = new Set(currentTags);
  for (const group of tagGroups) {
    const groupTags = currentTags.filter((tag) => tag.tagGroupId === group.id);
    if (groupTags.length === 0) {
      continue;
    }
    for (const tag of groupTags) {
      remaining.delete(tag);
    }
    buckets.push({ group, tags: groupTags });
  }
  const leftover = currentTags.filter((tag) => remaining.has(tag));
  if (leftover.length > 0) {
    buckets.push({ group: undefined, tags: leftover });
  }
  return buckets;
}

/**
 * Derives the `RedoBadge` props (color + primary/alternate type + label) for a
 * tag from its persisted `PillTheme`. Centralized here so the support-helpdesk
 * tag selector, the macros settings tag picker, and any other RedoList-driven
 * tag UI render badges identically and honor alternate-type themes.
 */
export function tagToBadgeProps(
  tag: TagResponse,
): Omit<RedoBadgeProps, "size"> {
  const pillTheme = tag.pillTheme ?? PillTheme.NORMAL;
  return {
    color: BadgePillThemeToColorMapping[pillTheme],
    type: BadgePillThemeToTypeMapping[pillTheme],
    text: tag.name,
  };
}

/**
 * Builds a flat list of section + badge items grouped by tag-group. Groups
 * whose tags don't appear in `tags` (post-filter) are omitted. Section header
 * items now omit `value` (see `SectionRedoListItem`); badge items carry the
 * full `TagResponse` so callers can react to selection.
 */
export function buildGroupedTagListItems(
  tags: readonly TagResponse[],
  tagGroups: readonly TagGroupResponse[],
): RedoListItem<TagResponse>[] {
  const tagsByGroup = new Map<ShopperAiTagGroupId, TagResponse[]>();
  for (const tag of tags) {
    const existing = tagsByGroup.get(tag.tagGroupId);
    if (existing) {
      existing.push(tag);
    } else {
      tagsByGroup.set(tag.tagGroupId, [tag]);
    }
  }

  const items: RedoListItem<TagResponse>[] = [];
  const assignedTagIds = new Set<ShopperAiTagId>();
  for (const group of tagGroups) {
    const groupTags = tagsByGroup.get(group.id);
    const firstTag = groupTags?.[0];
    if (!firstTag) {
      continue;
    }
    for (const tag of groupTags) {
      assignedTagIds.add(tag.id);
    }
    items.push({
      type: "section" as const,
      id: `group-${group.id}`,
      text: group.name,
      value: firstTag, // Unused as this is a section
    });
    for (const tag of groupTags) {
      items.push({
        type: "badge" as const,
        id: tag.id,
        badge: tagToBadgeProps(tag),
        selectionVariant: group.allowMultipleTags ? "checkbox" : "none",
        value: tag,
      });
    }
  }
  for (const tag of tags) {
    if (assignedTagIds.has(tag.id)) {
      continue;
    }
    items.push({
      type: "badge" as const,
      id: tag.id,
      badge: tagToBadgeProps(tag),
      selectionVariant: "checkbox",
      value: tag,
    });
  }
  return items;
}
